import { createApp } from 'vue';
import { VTooltip } from 'floating-vue';

import { createPinia } from 'pinia';
import axios from '@/includes/axios';
import App from './App.vue';
import router from './router';
import store from './store';
import i18n from './includes/i18n';
import VeeValidate from './includes/validation';
import FontAwesome from './includes/fontawesome';
import keycloak from '../keycloak';
import {
  getTenantFromHostname,
  isAdminPortal,
  isBareDomain,
  validateTenant,
  redirectToTenantError,
  redirectToNoDomain,
} from './services/tenantValidation';

import PrimeVue from 'primevue/config';
import aura from '@primevue/themes/aura';
import ToastService from 'primevue/toastservice';
import ConfirmationService from 'primevue/confirmationservice';
import 'primeicons/primeicons.css';
import './style.css';
import 'flag-icons/css/flag-icons.min.css';

import '@/assets/styles/theme.scss';

// import './assets/fonts/poppins.css';
import './assets/fonts/asenpro.css';
import 'floating-vue/dist/style.css';
import Tooltip from 'primevue/tooltip';
import Ripple from 'primevue/ripple';

const app = createApp(App);

// Ensure i18n is properly initialized
app.use(i18n);

app.use(PrimeVue, {
  theme: {
    preset: aura,
    options: {
      prefix: 'p',
      darkModeSelector: '.p-dark',
      cssLayer: false,
    },
  },
});
app.use(ToastService);
app.use(ConfirmationService);
app.directive('tooltip', VTooltip);
app.use(VeeValidate);
app.use(FontAwesome);
app.use(createPinia());
app.use(store);
app.directive('tooltip', Tooltip);
app.directive('ripple', Ripple);

// Function to mount app with proper error handling
const mountApp = async () => {
  try {
    // Load runtime configuration from backend before mounting
    // This enables the same build to work in single-tenant and multi-tenant modes
    await store.dispatch('runtimeConfig/loadConfig');

    // Validate tenant BEFORE mounting app (in multi-tenant mode)
    const multiTenantEnabled = store.getters['runtimeConfig/isMultiTenantEnabled'];

    // Sync to localStorage for components that need it outside Vuex (e.g., SideBar)
    localStorage.setItem('multiTenantEnabled', multiTenantEnabled ? 'true' : 'false');

    // Skip validation if already on error/no-domain page
    const currentPath = window.location.pathname;
    const isOnSpecialPage = currentPath === '/tenant-error' || currentPath === '/no-domain';

    if (multiTenantEnabled && !isAdminPortal() && !isOnSpecialPage) {
      // Check for bare domain access (no subdomain)
      if (isBareDomain()) {
        console.log('[Tenant Validation] Bare domain access, redirecting to no-domain page');
        redirectToNoDomain();
        return; // Stop mounting - page will redirect
      }

      const tenantSlug = getTenantFromHostname();

      if (tenantSlug) {
        console.log('[Tenant Validation] Validating tenant:', tenantSlug);
        const result = await validateTenant(tenantSlug);

        if (!result.valid) {
          console.log('[Tenant Validation] Invalid tenant, redirecting...');
          // Redirect using window.location (before Vue Router takes over)
          redirectToTenantError(result.error, tenantSlug, result.message);
          return; // Stop mounting - page will redirect
        }

        console.log('[Tenant Validation] Tenant is valid');
      }
    }

    // Add router guard for admin portal
    // Admin portal should only access /platform routes
    if (multiTenantEnabled && isAdminPortal()) {
      router.beforeEach((to, from, next) => {
        // Allow platform routes and tenant error page
        if (to.path.startsWith('/platform') || to.path === '/tenant-error') {
          return next();
        }
        // Redirect everything else to platform dashboard
        return next('/platform');
      });
    }

    app.use(axios);
    app.use(router);
    app.mount('#app');
  } catch (error) {
    console.error('Error mounting app:', error);
  }
};

if (import.meta.env.VITE_KEYCLOAK === 'true') {
  keycloak.init({
    checkLoginIframe: false,
    onLoad: 'check-sso',
    redirectUri: import.meta.env.VITE_KEYCLOAK_REDIRECT_URI,
  })
    .then(async () => {
      mountApp();
      setInterval(async () => {
        await keycloak.updateToken(import.meta.env.VITE_KEYCLOAK_TOKEN_MIN_VALIDITY_TIME);
      }, 5000);
    })
    .catch((error) => {
      console.error('Keycloak initialization failed:', error);
      // Fallback to non-keycloak mode
      mountApp();
    });
} else {
  mountApp();
}
